/**
 * D-020 Live Trading V2 — Kalshi line-availability classifier.
 *
 * One pure verdict per derivative line's Kalshi book, consumed by the
 * spread/total pill disables, the Poly-only hide filter, and the submit-time
 * intent guard (lib/trading/liveIntent.ts). Only 'active' may accept an
 * order; everything else is a distinct, explainable refusal.
 *
 * Freshness 'unknown'/undefined deliberately does NOT block: that is V1's
 * exact pill semantics (`freshness === 'stale'` disables; static and pre-game
 * books carry undefined freshness) — tightening it would kill pre-game
 * trading. Likewise an absent `status` field (older hydra frames, synthetic
 * books) is unknown, never 'suspended'.
 *
 * Status rule follows the codebase's own convention (marketStore's
 * isMarketActive: "treat any non-terminal status as live"): only
 * TERMINAL_STATUSES suspend. An unknown/novel hydra status string must never
 * silently kill order entry — the venue remains the authority on whether a
 * non-terminal market accepts orders (review finding, 2026-08-18).
 */
import type { VenueBook } from '@/views/ValueDashboard/types';
import { TERMINAL_STATUSES } from './marketLifecycle';

export type LineAvailability =
  | 'active'
  | 'suspended'
  | 'stale'
  | 'mapping-error'
  | 'absent';

export const LINE_AVAILABILITY_LABEL: Record<LineAvailability, string> = {
  active: 'Active',
  suspended: 'Suspended',
  stale: 'Stale',
  'mapping-error': 'Mapping error',
  absent: 'No Kalshi market',
};

/** The line's source proof is not continuous: a HELD book proof that is
 *  withdrawn (`bookSourceValidity`), or — for a bookless row, where the row
 *  rule governs — an unconfirmed price proof (`sourceValidity`). The one
 *  predicate the classifier, the submit guard's wording and the "awaiting
 *  source" chip share, so they cannot disagree about which proof governs. */
export function lineSourceUnconfirmed(kalshi: VenueBook | null | undefined): boolean {
  return kalshi?.bookSourceValidity === 'unconfirmed' || kalshi?.sourceValidity === 'unconfirmed';
}

export function classifyLineAvailability(
  kalshi: VenueBook | null | undefined,
): LineAvailability {
  // No Kalshi book at all — the Polymarket-only / unlisted-line case. Must
  // never look executable (LT2-005).
  if (kalshi == null) return 'absent';
  // A book object with no ticker cannot resolve to a venue market — that is a
  // mapping defect, not venue absence. Fail closed and keep it diagnosable.
  if (typeof kalshi.marketTicker !== 'string' || kalshi.marketTicker.trim() === '')
    return 'mapping-error';
  if (kalshi.status != null && TERMINAL_STATUSES.has(kalshi.status)) return 'suspended';
  // Availability follows the proof that governs the numbers on the line: a
  // held-but-withdrawn book proof is stale regardless of any independent
  // ticker proof; a bookless row keeps the row rule (freshness / price proof).
  // `tickerSourceValidity` is deliberately NOT consulted — the ticker scalars
  // (last price, volume) never decide whether the book will execute.
  if (kalshi.bookSourceValidity === 'unconfirmed') return 'stale';
  if (kalshi.freshness === 'stale' || kalshi.sourceValidity === 'unconfirmed') return 'stale';
  return 'active';
}
